

import { useEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import process_vars from '../../intermediate/process_vars';
import Modal from '../../utils/Modal';
import execute_script from './execute_script';

// Backing-store width of the drawing canvas. The height follows the element's
// aspect ratio so the saved image is not distorted when the record renders it
// stretched to the element's box.
const CANVAS_WIDTH = 1000;

// Aspect ratio (width / height) of the element, used to shape both the drawing
// canvas and its on-screen box. Sizes may carry variables, so they go through
// process_vars first, and a broken/zero size falls back to a wide strip.
function aspectRatioOf(comps, id, vars) {
    let width = parseFloat(process_vars(comps[id].size[0], vars));
    let height = parseFloat(process_vars(comps[id].size[1], vars));
    if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
        return 2;
    }
    return width / height;
}

export default function Signature({ id, comps, setter, vw, vars, setVars, editable, pdf, externals, setExternals, recordName, setRecordName }) {

    const [isSigning, setIsSigning] = useState(false);

    // The PDF/export path renders without a setter, so treat it as read-only.
    const canSign = editable && !comps[id].disable && typeof setter === 'function';

    let innerContent = <p style={{
        width: '90%',
        textAlign: 'center',
        fontWeight: '300',
        color: process_vars(comps[id].props.color, vars)
    }}>
        {canSign ? 'Click to Sign' : 'Signature Region'}
    </p>;

    if (comps[id].props.data) {
        innerContent = <img src={comps[id].props.data} draggable="false" alt="signature" style={{
            width: '100%',
            height: '100%'
        }} onClick={(e) => {
            e.preventDefault();
        }}></img>;
    }

    let boxShadow = "0px 0px 0px #ff4f42";
    if (comps[id].error) {
        boxShadow = '0px 0px 50px #ff4f42';
    }
    else if (comps[id].hasOwnProperty('warning')) {
        if (comps[id].warning) {
            boxShadow = `0px 0px 50px #d35400`;
        }
    }
    else if (comps[id].disable) {
        boxShadow = '0px 0px 50px #4e6df5';
    }

    // The record payload carries the signature as a data URL on props.data; the
    // backend turns it into a stored image and hands back an image_id on reload.
    const saveSignature = (dataUrl) => {
        let new_comps = [...comps];
        new_comps[id].props.data = dataUrl;
        setter(new_comps);
        execute_script(1, id, comps, setter, vars, setVars, externals, setExternals, recordName, setRecordName);
        setIsSigning(false);
    };

    return (
        <div>
            {pdf == true ? (
                <div style={{
                    border: process_vars(comps[id].props.borderEnabled, vars) ?
                        `${process_vars(comps[id].props.borderColor, vars)} solid ${process_vars(comps[id].props.borderWidth)}pt` : '#000 solid 0px',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: process_vars(comps[id].size[0], vars) * vw,
                    height: process_vars(comps[id].size[1], vars) * vw,
                    transform: `translate(${process_vars(comps[id].position[0], vars) * vw}px, ${process_vars(comps[id].position[1], vars) * vw}px)`

                }}>
                    {innerContent}
                </div>
            ) : (
                <Rnd className='comp' style={{
                    backgroundColor: process_vars(comps[id].props.backgroundColor, vars),
                    textAlign: 'center',
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: 'hidden',
                    zIndex: '0',
                    border: process_vars(comps[id].props.borderEnabled, vars) ?
                        `${process_vars(comps[id].props.borderColor, vars)} solid ${process_vars(comps[id].props.borderWidth)}pt` : '#000 solid 0px',
                    position: 'absolute',
                    boxShadow: editable ? boxShadow : 'none',
                    transition: 'box-shadow 0.5s',
                    cursor: canSign ? 'pointer' : 'default'
                }} position={{
                    x: process_vars(comps[id].position[0], vars) * vw,
                    y: process_vars(comps[id].position[1], vars) * vw
                }} size={{
                    width: process_vars(comps[id].size[0], vars) * vw,
                    height: process_vars(comps[id].size[1], vars) * vw
                }} enableResizing={false} disableDragging={true}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (canSign) {
                            setIsSigning(true);
                        }
                    }}>{innerContent}</Rnd>
            )}
            {isSigning ?
                <Modal width='60' height='65'>
                    <SignaturePad
                        aspectRatio={aspectRatioOf(comps, id, vars)}
                        penColor={process_vars(comps[id].props.color, vars) || '#000000'}
                        initialData={comps[id].props.data}
                        onSave={saveSignature}
                        onExit={() => setIsSigning(false)}
                    />
                </Modal>
                : <></>}
        </div>
    );

}

// Free-hand drawing surface. Pointer events cover mouse, touch and stylus in one
// path; `touchAction: none` stops the browser panning the page mid-stroke.
function SignaturePad({ aspectRatio, penColor, initialData, onSave, onExit }) {

    const canvasRef = useRef(null);
    const isDrawing = useRef(false);
    const lastPoint = useRef(null);
    const [hasInk, setHasInk] = useState(false);

    const canvasHeight = Math.max(1, Math.round(CANVAS_WIDTH / aspectRatio));

    // Draw whatever the element already holds onto the canvas, so re-opening the
    // pad shows the existing signature instead of a blank sheet.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
        if (!initialData) return;

        const image = new Image();
        image.onload = () => {
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            setHasInk(true);
        };
        image.src = initialData;
    }, [initialData]);

    // Canvas pixels per CSS pixel — the canvas is displayed scaled to fit the
    // modal, so pointer coordinates have to be mapped back onto the backing store.
    const pointOf = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) * (canvas.width / rect.width),
            y: (e.clientY - rect.top) * (canvas.height / rect.height)
        };
    };

    const strokeStyle = (context) => {
        context.strokeStyle = penColor;
        context.fillStyle = penColor;
        context.lineWidth = 4;
        context.lineCap = 'round';
        context.lineJoin = 'round';
    };

    const startStroke = (e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Keeps the stroke attached to this canvas even if the pointer leaves it.
        try { canvas.setPointerCapture(e.pointerId); } catch (_) { }

        const context = canvas.getContext('2d');
        const point = pointOf(e);
        isDrawing.current = true;
        lastPoint.current = point;

        // A tap with no movement should still leave a dot.
        strokeStyle(context);
        context.beginPath();
        context.arc(point.x, point.y, context.lineWidth / 2, 0, 2 * Math.PI);
        context.fill();
        setHasInk(true);
    };

    const continueStroke = (e) => {
        if (!isDrawing.current) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        const point = pointOf(e);
        strokeStyle(context);
        context.beginPath();
        context.moveTo(lastPoint.current.x, lastPoint.current.y);
        context.lineTo(point.x, point.y);
        context.stroke();
        lastPoint.current = point;
    };

    const endStroke = (e) => {
        if (!isDrawing.current) return;
        isDrawing.current = false;
        lastPoint.current = null;
        try { canvasRef.current.releasePointerCapture(e.pointerId); } catch (_) { }
    };

    const clear = (e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        setHasInk(false);
    };

    const save = (e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;
        // PNG keeps the background transparent, so the element's own background
        // colour shows through behind the signature.
        onSave(canvas.toDataURL('image/png'));
    };

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '2vh 2vh 0 2vh',
            boxSizing: 'border-box'
        }}>
            <p style={{
                fontSize: '1.2vmax',
                fontWeight: '300',
                marginBottom: '0.5vh'
            }}>Signature</p>
            <p style={{
                fontSize: '0.85vmax',
                fontWeight: '300',
                opacity: '0.7',
                marginBottom: '1.5vh'
            }}>Draw below with a mouse, finger or stylus.</p>

            <div style={{
                // Capping the width at (height x ratio) keeps the box inside the
                // modal while the canvas's own proportions set the height.
                width: '90%',
                maxWidth: `${38 * aspectRatio}vh`,
                margin: '0 auto'
            }}>
                <canvas
                    ref={canvasRef}
                    width={CANVAS_WIDTH}
                    height={canvasHeight}
                    onPointerDown={startStroke}
                    onPointerMove={continueStroke}
                    onPointerUp={endStroke}
                    onPointerCancel={endStroke}
                    style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        backgroundColor: '#ffffff',
                        border: '1px #FAF3DD solid',
                        borderRadius: '2px',
                        touchAction: 'none',
                        cursor: 'crosshair'
                    }}
                />
            </div>

            <div style={{
                marginTop: 'auto',
                paddingBottom: '1vmax',
                display: 'flex',
                justifyContent: 'center',
                gap: '0.75vmax'
            }}>
                <a href='' className='panel-button' onClick={clear}>Clear</a>
                <a href='' className='panel-button' style={{
                    opacity: hasInk ? '1' : '0.4'
                }} onClick={(e) => {
                    if (!hasInk) {
                        e.preventDefault();
                        return;
                    }
                    save(e);
                }}>Save</a>
                <a href='' className='panel-button' onClick={(e) => {
                    e.preventDefault();
                    onExit();
                }}>Exit</a>
            </div>
        </div>
    );

}
